import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import './customDropdown.css';

export type DropdownOption<TValue extends string | number> = {
  value: TValue;
  label: string;
};

type CustomDropdownProps<TValue extends string | number> = {
  id?: string;
  label?: string;
  placeholder?: string;
  options: DropdownOption<TValue>[];
  value?: TValue;
  selectedLabelOverride?: string;
  isInvalid?: boolean;
  onChange: (value: TValue | undefined) => void;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  allOptionLabel?: string;
  showAllOption?: boolean;
  triggerIcon?: ReactNode;
};

const CustomDropdown = <TValue extends string | number>({
  id,
  label,
  placeholder = 'Select value',
  options,
  value,
  selectedLabelOverride,
  isInvalid = false,
  onChange,
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  allOptionLabel = 'All',
  showAllOption = true,
  triggerIcon,
}: CustomDropdownProps<TValue>) => {
  const generatedId = useId();
  const dropdownId = id ?? `custom-dropdown-${generatedId}`;
  const listboxId = `${dropdownId}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const [menuStyles, setMenuStyles] = useState<CSSProperties>({});

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    setMenuStyles({
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 9999,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    const handleScrollOrResize = () => {
      updateMenuPosition();
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current &&
        !rootRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  const selectedLabel = useMemo(() => {
    if (value === undefined) {
      return showAllOption ? allOptionLabel : placeholder;
    }

    return (
      selectedLabelOverride ??
      options.find((option) => option.value === value)?.label ??
      placeholder
    );
  }, [allOptionLabel, options, placeholder, selectedLabelOverride, showAllOption, value]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) {
      return options;
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
  }, [options, searchable, searchTerm]);

  const optionValues = useMemo(() => {
    if (showAllOption) {
      return [undefined, ...filteredOptions.map((option) => option.value)] as Array<
        TValue | undefined
      >;
    }

    return filteredOptions.map((option) => option.value) as Array<TValue | undefined>;
  }, [filteredOptions, showAllOption]);

  const focusOption = (index: number) => {
    optionRefs.current[index]?.focus();
  };

  const moveActiveOption = (nextIndex: number) => {
    const safeIndex = Math.max(0, Math.min(nextIndex, optionValues.length - 1));
    setActiveIndex(safeIndex);
    focusOption(safeIndex);
  };

  const toggleOpen = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleSelect = (nextValue: TValue | undefined) => {
    onChange(nextValue);
    setSearchTerm('');
    setIsOpen(false);
  };

  const openFromTrigger = (startFromEnd = false) => {
    if (disabled) {
      return;
    }

    setIsOpen(true);
    const selectedIndex = optionValues.findIndex((optionValue) => optionValue === value);
    const fallbackIndex = startFromEnd ? optionValues.length - 1 : 0;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : fallbackIndex);
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openFromTrigger();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openFromTrigger(true);
    }
  };

  const handleOptionKeyDown = (
    event: KeyboardEvent<HTMLLIElement>,
    index: number,
    optionValue: TValue | undefined
  ) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActiveOption(index + 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActiveOption(index - 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      moveActiveOption(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      moveActiveOption(optionValues.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect(optionValue);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (searchable) {
      searchInputRef.current?.focus();
      return;
    }

    focusOption(activeIndex);
  }, [activeIndex, isOpen, searchable]);

  useEffect(() => {
    optionRefs.current = [];
  }, [optionValues.length]);

  return (
    <div className="custom-dropdown" ref={rootRef}>
      {label && (
        <label htmlFor={dropdownId} className="custom-dropdown__label">
          {label}
        </label>
      )}

      <button
        id={dropdownId}
        ref={triggerRef}
        type="button"
        className={`custom-dropdown__trigger ${disabled ? 'is-disabled' : ''} ${
          isInvalid ? 'is-invalid' : ''
        }`}
        onClick={toggleOpen}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        disabled={disabled}
      >
        <span className="custom-dropdown__trigger-value">{selectedLabel}</span>
        {triggerIcon ?? (
          <span className={`custom-dropdown__chevron ${isOpen ? 'is-open' : ''}`}>▾</span>
        )}
      </button>

      {isOpen &&
        createPortal(
          <div className="custom-dropdown__menu" ref={menuRef} style={menuStyles}>
            {searchable && (
              <input
                ref={searchInputRef}
                className="custom-dropdown__search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    moveActiveOption(event.key === 'ArrowDown' ? 0 : optionValues.length - 1);
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    setIsOpen(false);
                  }
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
            )}

            <ul
              id={listboxId}
              className="custom-dropdown__list"
              role="listbox"
              aria-label={label ?? placeholder}
              aria-activedescendant={`${dropdownId}-option-${activeIndex}`}
            >
              {showAllOption && (
                <li
                  id={`${dropdownId}-option-0`}
                  ref={(element) => {
                    optionRefs.current[0] = element;
                  }}
                  className={`custom-dropdown__option ${value === undefined ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(undefined)}
                  onKeyDown={(event) => handleOptionKeyDown(event, 0, undefined)}
                  tabIndex={activeIndex === 0 ? 0 : -1}
                  role="option"
                  aria-selected={value === undefined}
                >
                  {allOptionLabel}
                </li>
              )}

              {filteredOptions.length === 0 ? (
                <li className="custom-dropdown__empty">No matching results</li>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionIndex = showAllOption ? index + 1 : index;
                  return (
                    <li
                      key={`${option.value}`}
                      id={`${dropdownId}-option-${optionIndex}`}
                      ref={(element) => {
                        optionRefs.current[optionIndex] = element;
                      }}
                      className={`custom-dropdown__option ${value === option.value ? 'is-selected' : ''}`}
                      onClick={() => handleSelect(option.value)}
                      onKeyDown={(event) => handleOptionKeyDown(event, optionIndex, option.value)}
                      tabIndex={activeIndex === optionIndex ? 0 : -1}
                      role="option"
                      aria-selected={value === option.value}
                    >
                      {option.label}
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDropdown;
